import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  IconButtonDirective,
  SpinnerDirective,
  TooltipDirective,
  UseArrowIconDirective,
  UseUIBasicIconDirective,
} from '../../../ui-components';
import { ChatsHelper } from '../../chats.helper';
import { ChatWithMessagesActions } from '../../store/chats.actions';
import { chatsFeature, ChatsState } from '../../store/chats.reducer';
import { SpeechToTextListeningComponent, Tokens } from './speech-to-text';
import { SpeechToTextServiceInterface } from './speech-to-text/prompt-input-speech-to-text.interfaces';

const TEXTAREA_MIN_HEIGHT = 28;
const TEXTAREA_MAX_HEIGHT = 272;
const MAX_USER_INPUT_LENGTH = 12000;

@Component({
  selector: 'dmcom-prompt-input',
  imports: [
    FormsModule,
    TranslateModule,
    IconButtonDirective,
    UseUIBasicIconDirective,
    SpeechToTextListeningComponent,
    TooltipDirective,
    SpinnerDirective,
    MatTooltipModule,
    UseArrowIconDirective,
  ],
  styles: [
    `
      .custom-placeholder {
        position: relative;
      }
      .custom-placeholder::placeholder {
        color: transparent;
      }
      .placeholder-overlay {
        position: absolute;
        top: 4px;
        left: 8px;
        pointer-events: none;
        color: var(--color-grayscale-gray-dark);
        font-size: 16px;
        line-height: 24px;
        font-family: inherit;
      }
      .placeholder-overlay .italic-text {
        font-style: italic;
      }
    `,
  ],

  template: `
    <div
      class="flex flex-col gap-4 bg-white rounded-2xl mx-auto my-auto relative border border-grayscale-gray-light"
      style="box-shadow: 0 2px 16px 0 rgba(0, 0, 0, 0.08);"
    >
      <!-- INPUT -->
      <div
        #container
        class="
          relative w-full
          p-12
        "
        [dmcomTooltip]="tooltipText() | translate"
        [alwaysOn]="isMessageTooLong()"
      >
        <div class="flex flex-col gap-16 justify-start w-full">
          <!-- MAIN CONTENT -->
          <div class="flex-1 flex flex-row items-center justify-start min-w-0">
            @if (isRecording()) {
              <div
                class="flex-1 flex flex-row gap-10 items-start justify-start min-w-0 pl-8"
              >
                <!-- VOICE RECORDING INDICATORS -->
                <dmcom-speech-to-text-listening
                  class="mt-4"
                  [isRecording]="isRecording()"
                  [isLoading]="!isSpeechToTextReady()"
                ></dmcom-speech-to-text-listening>
              </div>
            } @else {
              <!-- TEXT INPUT -->
              <div
                class="flex-1 flex flex-row gap-8 items-start justify-start min-w-0 pl-8 custom-placeholder"
              >
                <textarea
                  #textarea
                  [value]="userInput()"
                  (input)="handleInput($event)"
                  (keydown)="handleKeydown($event)"
                  (paste)="onPaste()"
                  rows="1"
                  class="
                      bg-transparent outline-none hide-scrollbar resize-none overflow-y-auto min-h-24
                      typo-p2 text-paragraphs w-full transition-all duration-25
                      focus:caret-accent disabled:cursor-not-allowed placeholder:text-grayscale-gray-dark pt-6
                    "
                  [disabled]="isRecording() || disabled()"
                  autofocus
                ></textarea>
                @if (!userInput()) {
                  <div class="placeholder-overlay">
                    Ask Compliance <span class="italic-text">Edge</span>
                  </div>
                }
              </div>
            }
          </div>

          <!-- BUTTONS -->
          <div
            class="flex flex-row gap-8 items-center justify-between shrink-0"
          >
            <button
              dmcomIconButton
              color="neutral"
              size="lgmd"
              [disabled]="true"
              matTooltip="{{ 'PROMPT_INPUT.TOOLTIP.UPLOAD_FILE' | translate }}"
              matTooltipPosition="above"
            >
              <svg dmcomUseUIBasicIcon="plus"></svg>
            </button>

            <div class="flex flex-row gap-8 items-center justify-end shrink-0">
              @if (isRecording()) {
                <!-- VOICE MODE BUTTONS -->
                <button
                  dmcomIconButton
                  color="primary"
                  size="lgmd"
                  (click)="cancelRecording()"
                >
                  <svg dmcomUseUIBasicIcon="close"></svg>
                </button>
                @if (isProcessingWithDelay()) {
                  <!-- PROCESSING SPINNER -->
                  <div dmcomSpinner color="primary" size="md"></div>
                } @else {
                  <!-- ACCEPT BUTTON -->
                  <button
                    dmcomIconButton
                    variant="filled"
                    color="primary"
                    size="lgxs"
                    (click)="acceptRecording()"
                  >
                    <svg dmcomUseUIBasicIcon="check"></svg>
                  </button>
                }
              } @else {
                <!-- DEFAULT MODE BUTTONS -->
                <button
                  dmcomIconButton
                  color="neutral"
                  size="lg"
                  [disabled]="
                    (!isSubmitDisabled() && !isRecording()) ||
                    isMessageTooLong() ||
                    disabled()
                  "
                  matTooltip="{{
                    'PROMPT_INPUT.TOOLTIP.VOICE_TO_TEXT' | translate
                  }}"
                  matTooltipPosition="above"
                  (click)="speechToTextService.startRecording()"
                >
                  <svg dmcomUseUIBasicIcon="mic"></svg>
                </button>

                <button
                  dmcomIconButton
                  color="primary"
                  variant="filled"
                  size="lgxs"
                  [disabled]="isSubmitDisabled() || isRecording()"
                  (click)="submitPrompt()"
                >
                  <svg dmcomUseArrowIcon="arrow-up"></svg>
                </button>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class PromptInputComponent implements OnInit {
  protected readonly speechToTextService = inject<SpeechToTextServiceInterface>(
    Tokens.SpeechToTextService,
  );

  public readonly isThinking = input.required<boolean>();
  public readonly container =
    viewChild<ElementRef<HTMLDivElement>>('container');
  public readonly textarea =
    viewChild<ElementRef<HTMLTextAreaElement>>('textarea');

  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly chatsHelper = inject(ChatsHelper);

  public readonly selectedChat = computed(() => {
    const selectedChat = this.chatsStore.selectSignal(
      chatsFeature.selectSelectedChat,
    );

    return {
      ...selectedChat(),
      title: this.chatsHelper.getChatTitle(selectedChat()?.title),
    };
  });
  public readonly isChatUnavailable = computed(() => {
    return false;
  });
  public readonly disabled = computed(() => this.isChatUnavailable());
  public readonly isMessageTooLong = signal(false);
  public readonly tooltipText = computed(() => {
    if (this.isChatUnavailable()) {
      return 'PROMPT_INPUT.TOOLTIP.CHAT_UNAVAILABLE';
    }

    if (this.isMessageTooLong()) {
      return 'PROMPT_INPUT.TOOLTIP.MESSAGE_TOO_LONG';
    }

    return '';
  });

  public readonly isSubmitDisabled = computed(() => {
    return (
      this.disabled() ||
      this.userInput().trim() === '' ||
      this.isMessageTooLong() ||
      this.isThinking()
    );
  });

  private readonly inputSubject = new Subject<void>();
  public readonly userInput = signal('');
  public readonly isRecording = this.speechToTextService.isRecording;
  public readonly isSpeaking = this.speechToTextService.isSpeaking;
  public readonly isSpeechToTextReady = this.speechToTextService.isReady;
  public readonly isSpeechProcessing = this.speechToTextService.isProcessing;
  public readonly isWaitingForProcessingComplete = signal(false);
  public readonly isProcessingWithDelay = signal(false);
  private processingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private previousChatId: number | null = null;

  constructor() {
    this.inputSubject.pipe(debounceTime(50)).subscribe(() => {
      setTimeout(() => this.adjustTextareaHeight(), 0);
    });

    // Speech processing complete effect
    effect(() => {
      if (this.isWaitingForProcessingComplete() && !this.isSpeechProcessing()) {
        this.isWaitingForProcessingComplete.set(false);
        this.handleProcessingComplete();
      }
    });

    // Debounced processing state effect
    effect(() => {
      const isProcessing =
        this.isSpeaking() ||
        this.isSpeechProcessing() ||
        this.isWaitingForProcessingComplete();

      if (this.processingTimeoutId !== null) {
        clearTimeout(this.processingTimeoutId);
        this.processingTimeoutId = null;
      }

      if (isProcessing) {
        this.isProcessingWithDelay.set(true);
      } else {
        this.processingTimeoutId = setTimeout(() => {
          this.isProcessingWithDelay.set(false);
          this.processingTimeoutId = null;
        }, 250);
      }
    });

    // Clear input when chat is changed
    effect(() => {
      const currentChat = this.selectedChat();
      const currentChatId = currentChat?.id ?? null;

      if (
        this.previousChatId !== null &&
        this.previousChatId !== currentChatId
      ) {
        this.clear();
      }

      this.previousChatId = currentChatId;
    });

    effect(() => {
      const userInput = this.userInput();
      this.isMessageTooLong.set(userInput.length > MAX_USER_INPUT_LENGTH);
    });
  }

  ngOnInit() {
    void this.speechToTextService.init();
  }

  handleInput(event: Event) {
    if (!this.isRecording()) {
      this.userInput.set((event.target as HTMLTextAreaElement).value);
      this.inputSubject.next();
    }
  }

  onPaste() {
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  adjustTextareaHeight(reset = false) {
    const textarea = this.textarea()?.nativeElement;
    if (!textarea) return;

    if (reset || !textarea.value) {
      textarea.style.height = `${TEXTAREA_MIN_HEIGHT}px`;
      return;
    }

    // Create a hidden clone to measure content
    const clone = textarea.cloneNode(true) as HTMLTextAreaElement;
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.height = 'auto';
    clone.style.minHeight = `${TEXTAREA_MIN_HEIGHT}px`;
    clone.style.maxHeight = 'none';
    clone.style.transition = 'none';
    clone.style.transitionDuration = '0s';
    clone.style.width = `${textarea.clientWidth}px`;
    clone.rows = 1;
    clone.value = textarea.value;

    document.body.appendChild(clone);
    const contentHeight = clone.scrollHeight;
    document.body.removeChild(clone);

    const newHeight = Math.min(contentHeight, TEXTAREA_MAX_HEIGHT);
    textarea.style.height = `${newHeight}px`;
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!this.isSubmitDisabled()) {
        this.submitPrompt();
      }
    }
  }

  addMarkdown(text: string): string {
    return text.replace(/\n/g, '  \n');
  }

  submitPrompt() {
    this.addChatMessage(this.addMarkdown(this.userInput()));
    this.clear();
  }

  clear() {
    this.userInput.set('');
    this.adjustTextareaHeight(true);
    this.cancelRecording();
  }

  private capitalizeFirstLetter(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  public cancelRecording(): void {
    this.speechToTextService.stopRecording();
    this.adjustTextareaHeight(true);
  }

  public acceptRecording(): void {
    if (!this.isSpeechProcessing()) {
      this.handleProcessingComplete();
    } else {
      this.isWaitingForProcessingComplete.set(true);
    }
  }

  private handleProcessingComplete(): void {
    const currentTranscription = this.speechToTextService.transcription();
    this.userInput.set(this.capitalizeFirstLetter(currentTranscription));
    setTimeout(() => this.adjustTextareaHeight(), 0);
    this.speechToTextService.stopRecording();
  }

  public addChatMessage(content: string): void {
    const selectedChat = this.selectedChat();
    if (!selectedChat?.id) {
      return;
    }

    this.chatsStore.dispatch(
      ChatWithMessagesActions.addChatMessage({
        chatId: selectedChat.id,
        content,
      }),
    );
  }
}
